import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ArrowRight, CalendarClock, Users2, Banknote, Landmark, KeyRound, X } from "lucide-react";
import api from "@/lib/api";
import NewPeriodModal from "@/components/NewPeriodModal";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import { formatKina } from "@/lib/money";
import { DASH, PROFILE } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

const SEED_NOTICE_ID = "seed_password_banner_v1";

function Kpi({ tid, label, value, color, accent, accentColor, icon: Icon }) {
    return (
        <div className="border border-neutral-200 bg-white p-5 rounded-[3px]" style={accent ? { borderTop: `3px solid ${accentColor || "var(--kp-red)"}` } : {}}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display">{label}</div>
                {Icon && <Icon size={16} className="text-neutral-400" strokeWidth={1.75} />}
            </div>
            <div data-testid={tid} className="font-display font-bold text-4xl mt-2 font-mono-money" style={{ color: color || "var(--kp-charcoal)" }}>
                {value}
            </div>
        </div>
    );
}

export default function Dashboard() {
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user?.role === "admin";
    const [dash, setDash] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [showChangePw, setShowChangePw] = useState(false);

    useEffect(() => {
        api.get("/dashboard").then(({ data }) => setDash(data));
    }, []);

    if (!dash) return <div className="p-10 text-neutral-500 text-sm">Loading dashboard…</div>;

    const seedBannerDismissed = user?.dismissed_notices?.includes(SEED_NOTICE_ID);
    const showSeedBanner = !!user?.password_is_seed && !seedBannerDismissed;

    const dismissSeedBanner = async () => {
        try {
            const { data } = await api.post("/auth/dismiss-notice", { notice_id: SEED_NOTICE_ID });
            setUser?.({ ...user, dismissed_notices: data.dismissed_notices });
        } catch {
            setUser?.({ ...user, dismissed_notices: [...(user?.dismissed_notices || []), SEED_NOTICE_ID] });
        }
    };

    const draft = dash.draft_period;
    const totals = draft?.totals || dash.last_approved?.totals || { gross: 0, tax: 0, net: 0 };
    const kpiSource = draft ? "draft" : dash.last_approved ? "last-approved" : "none";

    return (
        <div data-testid={DASH.page} className="p-10 max-w-[1300px]">
            {showSeedBanner && (
                <div
                    data-testid={PROFILE.seedBanner}
                    className="mb-6 flex items-start gap-3 px-4 py-3 border-l-2 rounded-[3px]"
                    style={{ background: "var(--kp-warn-bg, #FEF6E6)", borderLeftColor: "var(--kp-warn, #C88A17)", color: "#5A3600" }}
                >
                    <KeyRound size={16} className="mt-0.5 shrink-0" style={{ color: "var(--kp-warn, #C88A17)" }} />
                    <div className="flex-1 text-[13px] leading-relaxed">
                        You&rsquo;re still using a temporary password. Change it now from the profile menu.{" "}
                        <button
                            data-testid={PROFILE.seedBannerChangeBtn}
                            onClick={() => setShowChangePw(true)}
                            className="font-display font-semibold uppercase tracking-[0.12em] text-[11px] ml-1"
                            style={{ color: "var(--kp-red)" }}
                        >
                            Change password →
                        </button>
                    </div>
                    <button
                        data-testid={PROFILE.seedBannerDismiss}
                        onClick={dismissSeedBanner}
                        className="p-1 text-neutral-500 hover:text-black"
                        aria-label="Dismiss"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}
            <div className="flex items-start justify-between mb-2">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.24em] text-neutral-500 font-display">Dashboard</div>
                    <h1 className="font-display font-bold text-4xl mt-1 text-[color:var(--kp-charcoal)]">
                        Welcome back, {user?.name || user?.email?.split("@")[0]}.
                    </h1>
                    <p className="text-sm text-neutral-500 mt-1">
                        {draft ? (
                            <>Pay run for PPE <span className="font-mono-money">{draft.ppe_date}</span> is in progress · {dash.active_employees} staff on the books.</>
                        ) : dash.last_approved ? (
                            <>Last run PPE <span className="font-mono-money">{dash.last_approved.ppe_date}</span> is locked. Start the next fortnight when you&rsquo;re ready.</>
                        ) : (
                            <>No active pay run · click &ldquo;+ New Pay Period&rdquo; to start.</>
                        )}
                    </p>
                </div>
                {isAdmin && (
                    draft ? (
                        <button
                            data-testid={DASH.continueBtn}
                            onClick={() => navigate(`/pay-runs/${draft.id}`)}
                            className="inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px]"
                            style={{ background: "var(--kp-red)" }}
                        >
                            Continue pay run <ArrowRight size={15} />
                        </button>
                    ) : (
                        <button
                            data-testid={DASH.newPeriodBtn}
                            onClick={() => setShowNew(true)}
                            className="inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px]"
                            style={{ background: "var(--kp-red)" }}
                        >
                            <Plus size={16} strokeWidth={2.2} /> New pay period
                        </button>
                    )
                )}
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
                <Kpi tid={DASH.kpiStaff} label="Staff on the books" value={dash.active_employees} icon={Users2} />
                <Kpi tid={DASH.kpiGross} label={kpiSource === "draft" ? "Est. gross (draft)" : "Last gross"} value={formatKina(totals.gross)} icon={CalendarClock} />
                <Kpi tid={DASH.kpiTax} label={kpiSource === "draft" ? "Est. tax to remit" : "Last tax to remit"} value={formatKina(totals.tax)} color="var(--kp-red)" accent icon={Landmark} />
                <Kpi tid={DASH.kpiNet} label={kpiSource === "draft" ? "Est. net to pay" : "Last net paid"} value={formatKina(totals.net)} color="var(--kp-success)" accent accentColor="var(--kp-success)" icon={Banknote} />
            </div>

            {/* Recent periods */}
            <div className="mt-10">
                <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-display mb-3">Recent pay periods</div>
                {dash.recent_periods.length === 0 ? (
                    <div className="bg-white border border-dashed border-neutral-300 p-10 text-center rounded-[3px]">
                        <div className="text-sm text-neutral-500">No approved pay periods yet — the ledger starts here.</div>
                    </div>
                ) : (
                    <div className="bg-white border border-neutral-200 rounded-[3px]">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-neutral-500 font-display" style={{ background: "#FAFAFA" }}>
                                    <th className="px-5 py-3">PPE</th>
                                    <th className="px-5 py-3">Approved by</th>
                                    <th className="px-5 py-3 text-right">Gross</th>
                                    <th className="px-5 py-3 text-right">Tax</th>
                                    <th className="px-5 py-3 text-right">Net</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dash.recent_periods.map((p) => (
                                    <tr
                                        key={p.id}
                                        data-testid={DASH.recentRow(p.id)}
                                        className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer"
                                        onClick={() => navigate(`/pay-runs/${p.id}/complete`)}
                                    >
                                        <td className="px-5 py-3 font-mono-money text-[color:var(--kp-charcoal)] font-medium">{p.ppe_date}</td>
                                        <td className="px-5 py-3 text-neutral-600 text-xs">{p.approved_by || "—"}</td>
                                        <td className="px-5 py-3 text-right font-mono-money">{formatKina(p.totals?.gross)}</td>
                                        <td className="px-5 py-3 text-right font-mono-money" style={{ color: "var(--kp-red)" }}>{formatKina(p.totals?.tax)}</td>
                                        <td className="px-5 py-3 text-right font-mono-money" style={{ color: "var(--kp-success)" }}>{formatKina(p.totals?.net)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <NewPeriodModal
                open={showNew}
                onClose={() => setShowNew(false)}
                onCreated={(p) => { setShowNew(false); navigate(`/pay-runs/${p.id}`); }}
            />
            <ChangePasswordModal open={showChangePw} onClose={() => setShowChangePw(false)} />
        </div>
    );
}
